import { Box, Button, Image, Link, Text, useToast } from '@chakra-ui/react';
import { BrokerType, brokerTypesData, freedomTypes } from 'enums/shared/brokerType';
import { Form, Formik } from 'formik';
import useBrokerLogo from 'hooks/modules/brokerAccount/useBrokerLogo';
import useFormError from 'hooks/shared/useFormError';
import ConnectBrokerInstructionItem from 'presentation/components/modules/brokerAccount/create/ConnectBrokerInstructionItem';
import AppCard from 'presentation/components/shared/card/AppCard';
import FormTextInput from 'presentation/components/shared/form/FormTextInput';
import GridHeading from 'presentation/components/shared/grid/GridHeading';
import SettingsLayout from 'presentation/layout/modules/settings/SettingsLayout';
import { Color } from 'presentation/theme/colorScheme';
import { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import AppRoute from 'router/AppRoute';
import { useCreateBrokerAccountMutation } from 'rtkQuery/sharespro/queries/brokerAccounts';
import * as Yup from 'yup';

interface FormValues {
  public_key: string;
  secret_key: string;
}

interface Props {
  brokerType: BrokerType;
}

const ConnectTradernetBrokerPage: FC<Props> = ({ brokerType }) => {
  const { t } = useTranslation();

  const { freedomType } = useParams();

  const brokerLogo = useBrokerLogo({ isFullLogo: true });

  const [createBroker, { isLoading }] = useCreateBrokerAccountMutation();

  const navigate = useNavigate();

  const formErrorHandler = useFormError<FormValues>();

  const toast = useToast();

  const validationSchema = Yup.object().shape({
    public_key: Yup.string().required(t('fieldIsRequired') ?? ''),
    secret_key: Yup.string().required(t('fieldIsRequired') ?? ''),
  });

  const title =
    brokerType === BrokerType.Freedom
      ? t('connectFreedomTitle')
      : t('connectTradernetBrokerTitle');

  return (
    <SettingsLayout>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <GridHeading>{title}</GridHeading>
      <AppCard p="40px" mb="20px" w="100%">
        <Image src={brokerLogo({ brokerType, freedomType: freedomTypes[parseInt(freedomType as string)] })} w="200px" mb="20px" ml="120px" />
        <Formik<FormValues>
          initialValues={{
            public_key: '',
            secret_key: '',
          }}
          onSubmit={(values, formik) => {
            createBroker({
              broker_id: brokerTypesData[BrokerType.Tradernet].id,
              public_key: values.public_key,
              secret_key: values.secret_key,
            })
              .unwrap()
              .then(() => {
                toast({
                  description: t('brokerAccountWasCreated'),
                  status: 'success',
                  position: 'top-right',
                  isClosable: true,
                });
                navigate(AppRoute.portfolio);
              })
              .catch((err) => formErrorHandler(err, formik));
          }}
          validationSchema={validationSchema}
        >
          {({ handleChange, values }) => {
            const steps = [
              <Text key={0}>
                {t('tradernetBrokerConnectStep1_1')}
                <Link
                  href="https://tradernet.ru/authentication/login"
                  textDecoration="underline"
                  isExternal
                >
                  {t('tradernetBrokerConnectStep1_2')}
                </Link>
              </Text>,
              <Text key={1}>
                {t('tradernetBrokerConnectStep2_1')}
                <Link
                  href="https://tradernet.ru/tradernet-api/auth-api"
                  textDecoration="underline"
                  isExternal
                >
                  {t('tradernetBrokerConnectStep2_2')}
                </Link>
              </Text>,
              <Text key={2}>{t('tradernetBrokerConnectStep3')}</Text>,
              <Box key={3} pt="10px">
                <Text mb="35px">{t('tradernetBrokerConnectStep4')}</Text>
                <Box mb="35px">
                  <FormTextInput
                    name="public_key"
                    onChange={handleChange}
                    value={values.public_key}
                    type="password"
                    placeholder={t('apiPublicKey') ?? ''}
                    autoComplete="off"
                  />
                </Box>
                <FormTextInput
                  name="secret_key"
                  onChange={handleChange}
                  value={values.secret_key}
                  type="password"
                  placeholder={t('apiPrivateKey') ?? ''}
                  autoComplete="off"
                />
              </Box>,
            ];

            return (
              <Form>
                <Box position="relative" w="100%" mb="35px">
                  <Box>
                    <Box w="100%">
                      {steps.map((stepText, index) => (
                        <Box key={index} mb="25px">
                          <ConnectBrokerInstructionItem
                            hasFormFields={index === 3}
                            step={index + 1}
                          >
                            {stepText}
                          </ConnectBrokerInstructionItem>
                        </Box>
                      ))}
                    </Box>
                    <Box
                      position="absolute"
                      left="78px"
                      top="0"
                      bgColor={Color.Shade100}
                      w="2px"
                      h={(52 * steps.length)}
                    />
                  </Box>
                </Box>
                <Button ml="120px" type="submit" isLoading={isLoading}>
                  {t('continuePhrase')}
                </Button>
              </Form>
            );
          }}
        </Formik>
      </AppCard>
    </SettingsLayout>
  );
};

export default ConnectTradernetBrokerPage;
